import React, { PropsWithChildren, ReactNode } from 'react';
import { ComponentWithCssSelector, withCssSelector } from '../shared/withCssSelector';
import { PropsWithClassName } from '../tabselector/PropsWithClassName';
import styled from 'styled-components';
import { useDropdownMenu } from '../dropdown/useDropdownMenu';
import { Button, ButtonVariant } from '../button';
import { IconName } from '../icon';
import { IconPosition } from '../button/ButtonVariant';
import { DropdownSize } from '../dropdown/DropdownSize';
import { Menu } from '../dropdown/Menu';
import { DropdownToggleMenuItem } from './DropdownToggleMenuItem';

const StyledDropdownToggleBox = styled.div``;

// The trigger can be a square icon button, so the menu cannot take its width.
const StyledMenu = styled(Menu)`
    min-width: 200px;
    // A menu of actions is short and named; it sizes to its rows instead of
    // capping at 6.5 of them and putting a scrollbar next to three items.
    // ponytail: give it back the cap if a call site ever grows a long list.
    max-height: none;
    // Nothing here ever scrolls, so no scrollbar should flash while the row
    // track expands and the content is briefly taller than the box.
    overflow: hidden;
`;

export type DropdownToggleProps = {
	variant?: ButtonVariant;
	size?: DropdownSize;
	/** Trigger label. Omit for the icon-only ellipsis trigger. */
	label?: string;
	icon?: IconName;
	iconPosition?: IconPosition;
	/** Accessible name for the trigger. Needed whenever there is no `label`. */
	'aria-label'?: string;
	// Conditional items ({cond && <LinkButton/>}) are normal here; toArray drops the falsy ones.
	children: ReactNode;
};

function DropdownToggleWithRef({
	variant = 'secondary',
	size = 'compact',
	label,
	icon = 'ellipsis-v',
	iconPosition = 'left',
	children,
	className,
	'aria-label': ariaLabel,
// The trigger is a Button, so a forwarded ref lands on a <button>. The type
// used to say HTMLDivElement, which no ref ever was.
}: PropsWithClassName<DropdownToggleProps>, buttonExternalRef: React.ForwardedRef<HTMLButtonElement>) {

	const { isOpen, onToggle, onClose, triggerRef, menuProps, renderMenu } =
		useDropdownMenu<HTMLButtonElement>({ placement: 'bottom-end', triggerRef: buttonExternalRef });

	const items = React.Children.toArray(children).filter(Boolean).map((child, index) =>
		React.isValidElement(child)
			? <DropdownToggleMenuItem
				key={index}
				{...child.props}
				// Picking an action closes the menu, as any menu does.
				onClick={child.props.disabled ? undefined : (event: React.MouseEvent) => {
					child.props.onClick?.(event);
					onClose();
				}}
				$size={size}
			/>
			: child);

	return <>
		<Button
			ref={triggerRef}
			icon={icon}
			label={label}
			iconPosition={iconPosition}
			size={size}
			variant={variant}
			onClick={onToggle}
			active={isOpen}
			aria-label={ariaLabel}
			aria-haspopup={'menu'}
			aria-expanded={isOpen}
		/>

		{renderMenu(<StyledMenu
			{...menuProps}
			className={className}
			items={items}
			size={size}
			role={'menu'}
		/>)}
	</>;
}

// eslint-disable-next-line max-len
export const DropdownToggle = withCssSelector(React.forwardRef(DropdownToggleWithRef), StyledDropdownToggleBox.toString()) as unknown as ((
	props: PropsWithChildren<PropsWithClassName<DropdownToggleProps>> & { ref?: React.ForwardedRef<HTMLButtonElement> }
  ) => ReturnType<typeof DropdownToggleWithRef>) & ComponentWithCssSelector;
